import { combineLatest, Observable, of, iif, from, throwError } from 'rxjs';
import { switchMap, map, filter, tap, first, catchError, take } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { MmAlertMode, MmAlertService, MmAlertSize, MmAlertType, MmDialogService } from '@metromarkets/components';
import { StorageService } from '@metromarkets/sdk';

import { IAdvancedFormConfig } from '~shared/modules/advanced-form/models/advanced-form.model';
import { CountriesService } from '~shared/services/countries.service';
import { PageService } from '~shared/services/page.service';
import { IAddress, IAddressDB } from '~shared/modules/addresses/models/address.model';
import { State as CheckoutState } from '~app/modules/checkout/store';
import { RemovePreservedAddress } from '~app/modules/checkout/store/addresses';
import { CHECKOUT_LOCALSTORAGE_KEY } from '~app/modules/checkout/constants';

import { AddressBookHttpService } from '~shared/modules/addresses/services/address-book-http.service';
import { AddressBookUtil } from '../utils/address-book.util';
import { RemoveAddressDialogComponent } from '../components/remove-address-dialog/remove-address-dialog.component';
import { AccountService } from '../../../services/account.service';
import { ADDRESS_BOOK_PAGE_ALIAS, ADDRESS_DELETE_DISABLED, NOTIFICATIONS } from '../constants/address-book.constant';
import { AccountInfoService } from '~core/modules/identity/services/account-info.service';
import { GeneralHttpError } from '~shared/modules/error-handler';
import { TranslateService } from '@ngx-translate/core';
import { NUMBER_4000 } from '~shared/constants';

@Injectable()
export class AddressBookService {
  constructor (
    private store$: Store<CheckoutState>,
    private mmDialogService: MmDialogService,
    private accountService: AccountService,
    private accountInfoService: AccountInfoService,
    private countriesHttpService: CountriesService,
    private addressBookHttpService: AddressBookHttpService,
    private pageService: PageService,
    private storageService: StorageService,
    private alertService: MmAlertService,
    private translateService: TranslateService
  ) {}

  public reload(): void {
    this.addressBookHttpService.fetch();
  }

  public clear(): void {
    this.addressBookHttpService.clear();
  }

  public createAddress(address: IAddress): Observable<IAddress> {
    return this.addressBookHttpService.create(address).pipe(
      switchMap(() => this.handleAddressBookChanges(address, NOTIFICATIONS.addressAdded)),
    );
  }

  public updateAddress(address: IAddress, isUpdated: boolean): Observable<IAddress> {
    if (isUpdated) {
      return this.addressBookHttpService.update(address).pipe(
        switchMap(() => this.handleAddressBookChanges(address, NOTIFICATIONS.addressSaved)),
      );
    }

    return this.handleAddressBookChanges(address, NOTIFICATIONS.addressSavedWithoutChanges);
  }

  public deleteAddress(addressId: string): Observable<string> {
    return this.addressBookHttpService.delete(addressId).pipe(
      switchMap(() => this.handleAddressBookChanges(addressId, NOTIFICATIONS.addressRemoved)),
      catchError((err: GeneralHttpError) => {
        if (err.message === ADDRESS_DELETE_DISABLED) {
          this.showMaintenanceAlert();
        }
        return throwError(err);
      }),
    );
  }

  public getAddressList(): Observable<IAddress[]> {
    const { addresses$ } = this.addressBookHttpService;
    const { countries$ } = this.countriesHttpService;

    return combineLatest([addresses$, countries$]).pipe(
      map(([addresses, countries]) => addresses.map((address) => {
        return AddressBookUtil.populateAddress(address, countries);
      }))
    );
  }

  public getAddress(addressId: string): Observable<IAddress> {
    const { addresses$ } = this.addressBookHttpService;
    const { countries$ } = this.countriesHttpService;

    return combineLatest([addresses$, countries$]).pipe(
      switchMap(([addresses, countries]) => {
        const targetAddress = addresses.find((address) => address.id === addressId);
        return of(AddressBookUtil.populateAddress(targetAddress, countries));
      }),
    );
  }

  public getAdvancedFormConfig(): Observable<IAdvancedFormConfig> {
    const isEntrepreneur$ = this.accountInfoService.isEntrepreneur();
    const addresses$ = this.getAddressList();
    const { countries$ } = this.countriesHttpService;

    return combineLatest([isEntrepreneur$, addresses$, countries$]).pipe(
      map(([isEntrepreneur, addresses, countries]) => {
        return AddressBookUtil.createAdvancedFormConfig(isEntrepreneur, addresses, countries);
      }),
    );
  }

  public showDeleteAddressPopup(addressId: string): void {
    const deleteAddress$ = this.getAddress(addressId).pipe(
      first(),
      switchMap((address) => {
        const dialog = this.mmDialogService.open(RemoveAddressDialogComponent, {
          data: { address },
        });

        return dialog.afterClosed();
      }),
      filter(Boolean),
      switchMap(() => {
        this.clearPreservedAddressesStorage(addressId);
        return this.deleteAddress(addressId);
      }),
    );

    deleteAddress$.subscribe();
  }

  private clearPreservedAddressesStorage(addressId: string): void {
    const checkoutLocalStorageState: CheckoutState = this.storageService.local.get(CHECKOUT_LOCALSTORAGE_KEY);
    if (checkoutLocalStorageState) {
      const newCheckoutLocalStorageState = { ...checkoutLocalStorageState, preserved: null };
      this.storageService.local.set(CHECKOUT_LOCALSTORAGE_KEY, newCheckoutLocalStorageState);
    }

    this.store$.dispatch(new RemovePreservedAddress({ addressId }));
  }

  private handleAddressBookChanges<T>(observableData: T, message: string): Observable<T> {
    return this.pageService.currentPageAlias$.pipe(
      first(),
      switchMap((pageAlias) => iif(
        () => AddressBookUtil.isChildPage(pageAlias),
        from(this.pageService.navigateByPageAlias(ADDRESS_BOOK_PAGE_ALIAS)),
        of(false),
      )),
      map(() => observableData),
      tap(() => this.accountService.showContextAlert(message)),
    );
  }

  private showMaintenanceAlert(): void {
    const title = 'CORE.ALL_PAGES.UNDER_MAINTENANCE_LABEL';
    const message = 'PAGES.ACCOUNT.ADDRESS_BOOK.ADDRESS_DELETE_DISABLED.ALERT.MESSAGE';
    this.translateService
        .get([title, message])
        .pipe(take(1))
        .subscribe((translations) => {
          this.alertService.addAlert({
            type: MmAlertType.warning,
            title: translations[title],
            message: translations[message],
            mode: MmAlertMode.banner,
            size: MmAlertSize.large,
          });
        });
  }
}
